import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { dashboardAPI } from "../../Api/dashboardAPI";
import { useAppDispatch } from "../../app/store";
import Modal from "../../components/Modal/Modal";
import Button from "../../components/reusable/Button/Button";
import Input from "../../components/reusable/Input/Input";
import TableList from "../../components/TableList/TableList";
import {
  getDetalleCuadratura,
  procesarCuadratura,
} from "../../features/Dashboard/AsyncThunks/cupos/getDetalleCuadratura";
import { getDetalleDeCupo } from "../../features/Dashboard/AsyncThunks/cupos/getDetalleDeCupo";
import {
  selectCuadraturaCupos,
  selectCuposAnalizados,
  selectCuposTotales,
  selectDetalleCupo,
  setCleanDetalleCupo,
} from "../../features/Dashboard/Slices/dashboardSlice";
import useAuth from "../../hooks/useAuth";
import { toastError, toastInfo, toastSuccess } from "../../utils/toast";
import "./Cupos.css";
import DetalleCupoCard from "./DetalleCupoCard";

function Cupos() {
  const cuposTotales = useSelector(selectCuposTotales);
  const cuposAnalizados = useSelector(selectCuposAnalizados);
  const [loading, setLoading] = useState(false);
  const [idCupo, setIdCupo] = useState<number | null>(null);
  const detalleCupo = useSelector(selectDetalleCupo);
  const cuadraturaCupo = useSelector(selectCuadraturaCupos);
  const dispatch = useAppDispatch();
  const { hasPermission } = useAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    message: "",
    action: () => {},
    actionLabel: "",
  });

  const updateCupo = async () => {
    if (idCupo) {
      setLoading(true);
      await dashboardAPI.actualizarCupo(idCupo);
      //await dispatch(getDetalleDeCupo(idCupo));
      toastSuccess("En unos instantes se actualizará la información");
      setLoading(false);
      setShowConfirmModal(false);
    } else {
      toastInfo("Ingrese idCupo");
    }
  };

  const syncCuposDescuadrados = async () => {
    setLoading(true);
    try {
      await dashboardAPI.syncCuposDescuadrados();
      toastSuccess("En unos instantes se actualizará la información");
    } catch (error: any) {
      console.log(error);
      toastError(error.message);
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const getCuadratura = async () => {
    setLoading(true);
    await dispatch(getDetalleCuadratura());
    setLoading(false);
  };

  const calcularCuadratura = async () => {
    setLoading(true);
    await dispatch(procesarCuadratura());
    setLoading(false);
    setShowConfirmModal(false);
  };

  const handleCalcularCuadratura = () => {
    setModalConfig({
      message: "¿Estás seguro de que deseas calcular la cuadratura?",
      action: calcularCuadratura,
      actionLabel: "Sí, calcular",
    });
    setShowConfirmModal(true);
  };

  const handleUpdateCupo = () => {
    if (!idCupo) {
      toastInfo("Ingrese idCupo");
      return;
    }
    setModalConfig({
      message: `¿Estás seguro de que deseas actualizar el cupo ${idCupo} desde BO?`,
      action: updateCupo,
      actionLabel: "Sí, actualizar",
    });
    setShowConfirmModal(true);
  };

  const handleSyncCuposDescuadrados = () => {
    setModalConfig({
      message:
        "¿Estás seguro de que deseas sincronizar los cupos descuadrados?",
      action: syncCuposDescuadrados,
      actionLabel: "Sí, sincronizar",
    });
    setShowConfirmModal(true);
  };

  const getDetalleCupo = async () => {
    if (idCupo) {
      setLoading(true);
      await dispatch(getDetalleDeCupo(idCupo));
      setLoading(false);
    } else {
      toastInfo("Ingrese idCupo");
    }
  };

  useEffect(() => {
    return () => {
      dispatch(setCleanDetalleCupo());
    };
  }, []);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    getDetalleCupo();
  };

  const accionesDeCupo = [
    {
      label: "Actualizar desde BO",
      action: handleUpdateCupo,
      description:
        "Sincroniza el cupo buscado desde Oracle hacia Cloud. Requiere un idCupo.",
      iconName: "cloud-download",
      disabled:
        !hasPermission("cupo", "actualizar_cupo") || loading || !idCupo,
      hint: !idCupo ? "Ingresá un idCupo y buscalo primero." : undefined,
    },
  ];

  const accionesDeCuadratura = [
    {
      label: "Consultar cuadratura",
      action: getCuadratura,
      description:
        "Obtiene el detalle de cuadratura y lo muestra en la tabla inferior.",
      iconName: "search",
      disabled: !hasPermission("cupo", "consultar_cuadratura") || loading,
    },
    {
      label: "Calcular cuadratura",
      action: handleCalcularCuadratura,
      description:
        "Recalcula la cuadratura de cupos. (Requiere confirmación)",
      iconName: "calculator",
      disabled: !hasPermission("cupo", "calcular_cuadratura") || loading,
    },
    {
      label: "Sincronizar descuadrados",
      action: handleSyncCuposDescuadrados,
      description:
        "Sincroniza los cupos que no cuadran. (Requiere confirmación)",
      iconName: "arrow-repeat",
      disabled:
        !hasPermission("cupo", "sincronizar_cupos_descuadrados") || loading,
    },
  ];

  return (
    <div className="p-6 grid grid-cols-4 gap-6">
      <div className="bg-white p-6 rounded-lg border-2 border-cardBorder col-span-3 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-textSecondary mb-3 text-[16px]">
            Cuadratura de cupos
          </h4>
          <form className="flex" onSubmit={handleSubmit}>
            <Input
              size="small"
              onChange={(text) => setIdCupo(Number(text))}
              type="number"
              placeholder="Ingrese idCupo (número)"
              className="rounded-r-none"
              disabled={!hasPermission("cupo", "buscar_cupo")}
            />
            <button
              type="submit"
              className="ml-[-3px] z-[1] w-28 text-white bg-primary rounded-r-md disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!hasPermission("cupo", "buscar_cupo")}
            >
              Buscar
            </button>
          </form>
        </div>
        {detalleCupo && <DetalleCupoCard detalle={detalleCupo} />}

        <div className="bg-white rounded-md p-4">
          <div className="flex">
            <h4 className="">Cuadratura de cupos</h4>
            <p className="flex-grow text-center font-semibold">
              Total: {cuposTotales} / Analizados: {cuposAnalizados}
            </p>
          </div>
          <hr className="text-secondary my-2" />
          {cuadraturaCupo.length > 0 ? (
            <TableList list={cuadraturaCupo} page="cupos" />
          ) : (
            <p className="text-secondary">
              No se encuentran datos de cuatratura
            </p>
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg border-2 border-cardBorder col-span-1 self-start">
        <h4 className="font-semibold text-textSecondary mb-3 text-[16px]">
          Acciones
        </h4>

        <p className="text-secondary text-[12px] font-semibold mb-2">
          Cupo {idCupo ? `#${idCupo}` : ""}
        </p>
        <div className="space-y-3 mb-5">
          {accionesDeCupo.map((btn) => (
            <div key={btn.label}>
              <Button
                label={btn.label}
                iconName={btn.iconName}
                onClick={btn.action}
                disabled={btn.disabled}
                outlined
                theme="default"
                className="w-full"
              />
              <p className="text-[11px] text-secondary mt-1">{btn.description}</p>
              {btn.hint && (
                <p className="text-[11px] text-secondary mt-0.5">{btn.hint}</p>
              )}
            </div>
          ))}
        </div>

        <p className="text-secondary text-[12px] font-semibold mb-2">
          Cuadratura
        </p>
        <div className="space-y-3">
          {accionesDeCuadratura.map((btn) => (
            <div key={btn.label}>
              <Button
                label={btn.label}
                iconName={btn.iconName}
                onClick={btn.action}
                disabled={btn.disabled}
                outlined
                theme="default"
                className="w-full"
              />
              <p className="text-[11px] text-secondary mt-1">{btn.description}</p>
            </div>
          ))}
        </div>
      </div>

      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        message={modalConfig.message}
        okButton={{
          label: modalConfig.actionLabel,
          buttonFn: modalConfig.action,
        }}
        closeButton={{
          label: "Cancelar",
          buttonFn: () => setShowConfirmModal(false),
        }}
        disabled={loading}
      />
    </div>
  );
}

export default Cupos;
